import type {
  ApiEnvelope,
  BusinessReviewSummary,
  Review,
  ReviewListQuery,
  ReviewListResponse,
  ReviewReply
} from './types'
import type { ReviewTransport } from './transport'

export interface ReviewClientOptions {
  transport: ReviewTransport
}

export interface CreateReviewInput {
  content_id: string | number
  content_type: string
  content_name?: string
  rating: number
  content: string
  user_email: string
  user_name?: string
  user_phone?: string
}

export interface UpdateReviewInput {
  rating: number
  content: string
  user_email: string
  user_name?: string
  user_phone?: string
}

export interface CreateReplyInput {
  parent_id: number
  content: string
  user_email: string
  content_name?: string
  user_name?: string
  user_phone?: string
}

export interface UpdateReplyInput {
  content: string
  user_email: string
  content_name?: string
  user_name?: string
  user_phone?: string
}

export interface UserEmailPayload {
  user_email: string
}

export interface ReviewClient {
  fetchReviews(
    contentId: string | number,
    query?: ReviewListQuery
  ): Promise<ApiEnvelope<ReviewListResponse>>
  fetchBusinessSummary(contentId: string | number): Promise<BusinessReviewSummary>
  createReview(payload: CreateReviewInput): Promise<ApiEnvelope<Review>>
  updateReview(reviewId: number, payload: UpdateReviewInput): Promise<ApiEnvelope<Review>>
  deleteReview(reviewId: number, payload: UserEmailPayload): Promise<ApiEnvelope<null>>
  markHelpful(reviewId: number, payload: UserEmailPayload): Promise<ApiEnvelope<{ status?: string; like_count?: number; dislike_count?: number }>>
  markDislike(reviewId: number, payload: UserEmailPayload): Promise<ApiEnvelope<{ status?: string; like_count?: number; dislike_count?: number }>>
  createReply(payload: CreateReplyInput): Promise<ApiEnvelope<ReviewReply>>
  updateReply(replyId: number, payload: UpdateReplyInput): Promise<ApiEnvelope<ReviewReply>>
  deleteReply(replyId: number, payload: UserEmailPayload): Promise<ApiEnvelope<null>>
}

export function createReviewClient({ transport }: ReviewClientOptions): ReviewClient {
  return {
    fetchReviews(contentId, query) {
      return transport.request<ApiEnvelope<ReviewListResponse>>({
        method: 'GET',
        url: `/reviews/contents/${contentId}`,
        params: {
          page: query?.page ?? 1,
          per_page: query?.perPage ?? 10
        }
      })
    },

    async fetchBusinessSummary(contentId: string | number) {
      const response = await transport.request<ApiEnvelope<BusinessReviewSummary>>({
        method: 'GET',
        url: `/reviews/contents/${contentId}/summary`
      })

      const inner = response.data ?? response.result ?? response

      if (inner && typeof inner === 'object' && 'reviews_avg_rating' in inner) {
        return inner as BusinessReviewSummary
      }

      return {
        reviews_avg_rating: '0',
        reviews_count: 0,
        rating_breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      }
    },

    createReview(payload) {
      return transport.request({
        method: 'POST',
        url: '/reviews',
        body: payload
      })
    },

    updateReview(reviewId, payload) {
      return transport.request({
        method: 'PUT',
        url: `/reviews/${reviewId}`,
        body: payload
      })
    },

    deleteReview(reviewId, payload) {
      return transport.request({
        method: 'DELETE',
        url: `/reviews/${reviewId}`,
        body: payload
      })
    },

    markHelpful(reviewId, payload) {
      return transport.request({
        method: 'POST',
        url: `/reviews/${reviewId}/helpful`,
        body: payload
      })
    },

    markDislike(reviewId, payload) {
      return transport.request({
        method: 'POST',
        url: `/reviews/${reviewId}/dislike`,
        body: payload
      })
    },

    createReply(payload) {
      return transport.request({
        method: 'POST',
        url: '/reviews/reply',
        body: payload
      })
    },

    updateReply(replyId, payload) {
      return transport.request({
        method: 'PUT',
        url: `/reviews/reply/${replyId}`,
        body: payload
      })
    },

    deleteReply(replyId, payload) {
      return transport.request({
        method: 'DELETE',
        url: `/reviews/reply/${replyId}`,
        body: payload
      })
    }
  }
}
